// Rocky readback producer for the Keyword Research area (Campaigns/*) — emits
// `keyword-research-screen-ready` postMessages so the embedded Rocky chatbot
// can see what the user sees on the keyword tabs. Transport lives in
// PopAssistantButton.vue (buffered postScreenToIframe, replayed on iframe
// load). Full payload contract for the Rocky side: ROCKY_KEYWORD_SCREENS.md
// (keep screen ids in sync with Rocky's formatter registry).
//
// Snapshots are built ONLY from what the UI itself renders: visible labels
// verbatim, IDs plus short previews — never API keys, tokens or full page
// bodies. Capped collections always ship `{ items, complete, totalCount }`;
// `complete: false` means authoritative-but-incomplete (distinct from absent).
import debounce from "lodash/debounce";
import { rockyTruncate } from "../FullPageSourceEditorV2/rockyReadback.js";

export const ROCKY_KEYWORD_SCREEN_MESSAGE_TYPE = "keyword-research-screen-ready";

export const KEYWORD_SCREENS = {
  STRATEGY_LIST: "strategy_list",
  STRATEGY_RESULTS: "strategy_results", // 1-click silo results table
  CUSTOM_SILO_DETAIL: "custom_silo_detail",
  SILO_CARDS: "silo_cards",
  SILO_DETAIL: "silo_detail",
  KEYWORD_INSIGHT_LIST: "keyword_insight_list",
  KEYWORD_INSIGHT_RESULTS: "keyword_insight_results",
  KEYWORD_TRACKING: "keyword_tracking",
};

// Contract bounds: ≤50 rows for keyword tables, ≤20 for lists/cards/topics,
// ≤200 chars per string (tag-stripped via rockyTruncate).
export const MAX_TABLE_ROWS = 50;
export const MAX_LIST_ITEMS = 20;

export const short = (s) => rockyTruncate(s, 200);

// Rocky's account scoping requires numeric ids; anything else is dropped
// server-side without logging, so refuse to emit non-numeric ids at all.
export function digitsOnlyId(v) {
  if (v === null || v === undefined) return null;
  const s = String(v);
  return /^\d{1,20}$/.test(s) ? s : null;
}

export function keywordTypeLabel(row) {
  if (!row) return "unlabeled";
  if (row.isTopLevelKeyword) return "top_level";
  if (row.isSecondaryKeyword) return "secondary_top_level";
  if (row.isSupportingKeyword) return "supporting";
  return "unlabeled";
}

export function summarize(rows, mapper, max = MAX_TABLE_ROWS) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    items: list.slice(0, max).map(mapper),
    complete: list.length <= max,
    totalCount: list.length,
  };
}

// Rows of `report.result` as rendered by KeywordResearchResult.vue
// (1-click silo results and Keyword Insight results). Trend/traffic columns
// are omitted: hidden by default in the table and heavy on prompt budget.
export function mapResearchResultRow(row) {
  const r = row || {};
  return {
    keyword: short(r.keyword),
    type: keywordTypeLabel(r),
    intent: r.keyword_intent != null ? r.keyword_intent : null,
    keywordScore: r.keywordScore != null ? r.keywordScore : null,
    kgrScore: r.KGRScore != null ? r.KGRScore : null,
    difficultyScore: r.difficultyScore != null ? r.difficultyScore : null,
    monthlySearchesLow: r.broadMatchMonthlySearches_LowEstimate != null ? r.broadMatchMonthlySearches_LowEstimate : null,
    monthlySearchesHigh: r.broadMatchMonthlySearches_HighEstimate != null ? r.broadMatchMonthlySearches_HighEstimate : null,
    aiSearchDemand: r.ai_search_demand != null ? r.ai_search_demand : null,
    sci: r.realCompMajesticTitleAndAnchorComp != null ? r.realCompMajesticTitleAndAnchorComp : null,
    allInTitle: r.allintitle != null ? r.allintitle : null,
    competitionStrength: r.competitionStrength != null ? r.competitionStrength : null,
    currentPosition: r.currentRankGoogle != null ? r.currentRankGoogle : null,
    currentRankingUrl: short(r.currentRankingUrl),
    source: r.source != null ? r.source : null,
    popScore: r.pageRunScore != null ? r.pageRunScore : null,
    hidden: !!r.isHidden,
  };
}

// Silo keyword rows as rendered by SiloResultTable.vue (SiloSingleView data);
// metrics live under `sk.keywordData`.
export function mapSiloKeywordRow(sk) {
  const s = sk || {};
  const kd = s.keywordData || {};
  return {
    siloKeywordId: digitsOnlyId(s.id),
    keyword: short(s.keyword),
    type: keywordTypeLabel(kd),
    intent: kd.keyword_intent != null ? kd.keyword_intent : null,
    keywordScore: kd.keywordScore != null ? kd.keywordScore : null,
    kgrScore: kd.KGRScore != null ? kd.KGRScore : null,
    difficultyScore: kd.difficultyScore != null ? kd.difficultyScore : null,
    monthlySearchesLow: kd.broadMatchMonthlySearches_LowEstimate != null ? kd.broadMatchMonthlySearches_LowEstimate : null,
    monthlySearchesHigh: kd.broadMatchMonthlySearches_HighEstimate != null ? kd.broadMatchMonthlySearches_HighEstimate : null,
    aiSearchDemand: kd.ai_search_demand != null ? kd.ai_search_demand : null,
    currentPosition: kd.currentRankGoogle != null ? kd.currentRankGoogle : null,
    currentRankingUrl: short(kd.currentRankingUrl),
    taScore: kd.ta_score != null ? kd.ta_score : null,
    opportunityScore: kd.opportunityScore != null ? kd.opportunityScore : null,
    page: short(kd.page),
    hasPopReport: s.pageRunId != null,
    popScore: s.pageRunResult && s.pageRunResult.pageRunScore != null ? s.pageRunResult.pageRunScore : null,
    deleted: !!s.isDeleted,
  };
}

// { schemaVersion, screen, siteId, campaignId, ...recordIds, status, loading, data }
// Returns null when a digits-only siteId can't be produced — Rocky's scoping
// would silently drop such a payload anyway, so we never send it.
export function buildEnvelope(vm, screen, { status, loading, recordIds, data } = {}) {
  const params = (vm.$route && vm.$route.params) || {};
  const siteId = digitsOnlyId(params.siteId);
  if (!siteId) return null;
  const envelope = {
    schemaVersion: 1,
    screen,
    siteId,
    campaignId: digitsOnlyId(params.campaignId),
    status: status || "ready",
    loading: !!loading,
    data: data || {},
  };
  Object.entries(recordIds || {}).forEach(([key, value]) => {
    envelope[key] = digitsOnlyId(value);
  });
  return envelope;
}

// The transport buffer in PopAssistantButton is a single slot per message
// type. Track the last emitter so a component being destroyed only clears
// the buffer when no sibling has emitted since — this makes the Silo
// list↔single swap (both under route name `silo`) and the results-table
// parent/child handoff order-independent.
let lastEmitterVm = null;

// Mixin. The consuming component must define `buildRockyScreenData()`
// returning `{ screen, status, loading, recordIds, data }`, or null to skip
// emitting (route-gated reusable components, states owned by a child).
export default {
  created() {
    // Per-instance debounce so sibling views never share a timer. Coalesces
    // search keystrokes / filter drags; load completions emit immediately.
    this._emitRockyKeywordDebounced = debounce(() => this.emitRockyKeywordScreen(), 1100);
  },
  beforeDestroy() {
    if (this._emitRockyKeywordDebounced) this._emitRockyKeywordDebounced.cancel();
    this.clearRockyKeywordScreen();
  },
  methods: {
    emitRockyKeywordScreen() {
      if (typeof this.buildRockyScreenData !== "function") return;
      const spec = this.buildRockyScreenData();
      if (!spec || !spec.screen) return;
      const envelope = buildEnvelope(this, spec.screen, spec);
      if (!envelope) return;
      this.$root.$refs.popAssistantButton?.postScreenToIframe(
        ROCKY_KEYWORD_SCREEN_MESSAGE_TYPE,
        envelope
      );
      lastEmitterVm = this;
    },
    emitRockyKeywordScreenDebounced() {
      if (this._emitRockyKeywordDebounced) this._emitRockyKeywordDebounced();
    },
    clearRockyKeywordScreen() {
      if (lastEmitterVm !== this) return;
      lastEmitterVm = null;
      this.$root.$refs.popAssistantButton?.clearScreenFromIframe(
        ROCKY_KEYWORD_SCREEN_MESSAGE_TYPE
      );
    },
  },
};
